import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BarChart3,
  Calculator,
  Calendar,
  ChevronRight,
  Crown,
  Flag,
  Loader2,
  TrendingUp,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AdPlacement } from "@/components/ads/AdPlacement";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { F1SeoContent } from "../components/F1SeoContent";
import {
  useConstructors,
  useCurrentSeason,
  useDriverStandings,
  useDrivers,
  useRaces,
  useSeasons,
} from "../hooks/use-f1-data";
import { getTeamColor } from "../lib/team-colors";

// Skeleton component for loading states
function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-muted/50 rounded ${className}`}
      aria-hidden="true"
    />
  );
}

export function F1FantasyHome() {
  // Get current season from the seasons list
  const currentSeason = useCurrentSeason();

  // Fetch data using hooks
  const { data: _seasons, isLoading: seasonsLoading } = useSeasons();
  const { data: races, isLoading: racesLoading } = useRaces(currentSeason);
  const { data: driverStandings, isLoading: standingsLoading } =
    useDriverStandings(currentSeason);
  const { data: seasonDrivers, isLoading: driversLoading } =
    useDrivers(currentSeason);
  const { data: seasonConstructors, isLoading: constructorsLoading } =
    useConstructors(currentSeason);

  // Live clock — null on the server + first client render (so hydration matches
  // the existing behaviour), then ticks every 30s once mounted so the countdown
  // actually counts down instead of freezing at its initial value.
  const [nowMs, setNowMs] = useState<number | null>(null);
  useEffect(() => {
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Calculate next race info
  const nextRace = useMemo(() => {
    if (!races || races.length === 0) {
      return null;
    }

    const now = nowMs != null ? new Date(nowMs) : new Date();
    const upcomingRaces = races
      .filter((race) => {
        if (!race.date) return false;
        const raceDate = new Date(race.date);
        return raceDate > now;
      })
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateA - dateB;
      });

    if (upcomingRaces.length === 0) {
      // If no upcoming races, show the last race of the season
      const lastRace = races[races.length - 1];
      if (!lastRace) return null;

      const lastRaceDate = lastRace.date ? new Date(lastRace.date) : null;
      return {
        name: lastRace.name,
        circuit: lastRace.name, // Use race name as circuit fallback
        date: lastRaceDate
          ? lastRaceDate.toLocaleDateString(getLocale(), {
              month: "long",
              day: "numeric",
              year: "numeric",
            })
          : "TBD",
        countdown: { days: 0, hours: 0, minutes: 0 },
        round: lastRace.round,
        isPast: true,
      };
    }

    const nextRaceData = upcomingRaces[0];
    const raceDate = nextRaceData.date
      ? new Date(nextRaceData.date)
      : new Date();
    const diff = raceDate.getTime() - now.getTime();

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return {
      name: nextRaceData.name,
      circuit: nextRaceData.name, // Use race name as circuit fallback
      date: raceDate.toLocaleDateString(getLocale(), {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      countdown: { days, hours, minutes },
      round: nextRaceData.round,
      isPast: false,
    };
  }, [races, nowMs]);

  // Calculate quick stats
  const quickStats = useMemo(() => {
    // Use actual driver/constructor counts from season data; fall back to standings-derived counts
    const totalDrivers = seasonDrivers?.length || driverStandings?.length || 0;
    const uniqueConstructors =
      seasonConstructors?.length ||
      new Set(driverStandings?.map((s) => s.constructorSlug).filter(Boolean))
        .size ||
      0;
    const totalRaces = races?.length ?? 0;
    const completedRaces =
      races?.filter((r) => r.date && new Date(r.date) < new Date()).length ?? 0;
    const remainingRaces = totalRaces - completedRaces;

    return [
      {
        label: m.f1_home_stat_drivers(),
        value: totalDrivers,
        icon: Users,
        loading: driversLoading,
      },
      {
        label: m.f1_home_stat_constructors(),
        value: uniqueConstructors,
        icon: Flag,
        loading: constructorsLoading,
      },
      {
        // Disambiguate: "Races Left" when the season is live, "Races" (total)
        // once it's over — instead of silently showing the total under the
        // same label.
        label:
          remainingRaces > 0
            ? m.f1_home_stat_races_left()
            : m.f1_home_stat_races(),
        value: remainingRaces > 0 ? remainingRaces : totalRaces,
        icon: Calendar,
        loading: racesLoading,
      },
      {
        label: m.f1_home_stat_season(),
        value: currentSeason,
        icon: Trophy,
        loading: seasonsLoading,
      },
    ];
  }, [
    driverStandings,
    seasonDrivers,
    seasonConstructors,
    races,
    currentSeason,
    driversLoading,
    constructorsLoading,
    racesLoading,
    seasonsLoading,
  ]);

  // Get top 4 drivers
  const topDrivers = useMemo(() => {
    if (!driverStandings) return [];

    return driverStandings
      .filter((s) => s.driverFullName && s.position <= 4)
      .sort((a, b) => a.position - b.position)
      .slice(0, 4)
      .map((standing) => ({
        name: standing.driverFullName ?? "Unknown",
        team: standing.constructorName ?? "Unknown Team",
        points: standing.points,
        wins: standing.wins,
        podiums: standing.podiums,
        position: standing.position,
        color: getTeamColor(standing.constructorSlug ?? ""),
      }));
  }, [driverStandings]);

  return (
    <div>
      {/* Hero Section with Race Countdown */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-destructive/10 via-transparent to-primary/10" />
          <div className="absolute inset-0 grid-pattern opacity-20" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Welcome & Quick Actions */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/f1-car-side-small.png"
                  alt=""
                  width="64"
                  height="64"
                  className="w-16 h-16 object-contain"
                />
                <span className="text-xs font-mono text-primary uppercase tracking-wider">
                  F1 Fantasy
                </span>
              </div>

              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                {m.f1_home_hero_line1()}
                <span className="block text-primary text-glow-cyan">
                  {m.f1_home_hero_line2()}
                </span>
              </h1>

              <p className="text-lg text-muted-foreground mb-4 max-w-lg">
                {m.f1_home_hero_subtitle()}
              </p>
              <p className="text-sm text-muted-foreground/80 mb-4 md:mb-8 max-w-lg">
                {m.f1_home_tools_description()}
              </p>

              {/* Quick action buttons */}
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/f1-fantasy/apex-team"
                  className="group inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 hover:scale-105"
                >
                  <Calculator className="h-4 w-4" />
                  {m.f1_home_card_apex_title()}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/f1-fantasy/budget-boost"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-border bg-card/50 text-foreground font-medium transition-all duration-300 hover:border-primary/50 hover:bg-card"
                >
                  <TrendingUp className="h-4 w-4" />
                  {m.f1_home_card_budget_title()}
                </Link>
              </div>
            </div>

            {/* Right: Next Race Countdown */}
            <div className="relative">
              <div className="relative p-6 md:p-8 rounded-2xl border border-border bg-card/80 backdrop-blur-sm overflow-hidden">
                {/* Racing stripe */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-destructive via-secondary to-success" />

                {racesLoading ? (
                  <div className="py-8">
                    <div className="flex items-center justify-center gap-2 mb-6">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">
                        {m.f1_home_loading_race()}
                      </span>
                    </div>
                    <Skeleton className="h-8 w-48 mx-auto mb-4" />
                    <Skeleton className="h-4 w-32 mx-auto mb-6" />
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="text-center p-4 rounded-lg bg-muted/50 border border-border"
                        >
                          <Skeleton className="h-10 w-16 mx-auto mb-2" />
                          <Skeleton className="h-3 w-12 mx-auto" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : nextRace ? (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <Flag className="h-5 w-5 text-destructive" />
                        <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                          {nextRace.isPast
                            ? m.f1_home_last_race()
                            : m.f1_home_next_race()}{" "}
                          • {m.f1_home_round({ round: String(nextRace.round) })}
                        </span>
                      </div>
                      <span
                        className={`status-dot ${nextRace.isPast ? "status-dot-inactive" : "status-dot-active"}`}
                      />
                    </div>

                    <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                      {nextRace.name}
                    </h2>

                    {/* Countdown */}
                    {!nextRace.isPast && (
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        {[
                          {
                            value: nextRace.countdown.days,
                            label: m.f1_home_countdown_days(),
                          },
                          {
                            value: nextRace.countdown.hours,
                            label: m.f1_home_countdown_hours(),
                          },
                          {
                            value: nextRace.countdown.minutes,
                            label: m.f1_home_countdown_mins(),
                          },
                        ].map((item) => (
                          <div
                            key={item.label}
                            className="text-center p-4 rounded-lg bg-muted/50 border border-border"
                          >
                            <div className="font-display text-3xl md:text-4xl font-bold text-primary">
                              {item.value}
                            </div>
                            <div className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                              {item.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {nextRace.isPast && (
                      <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border text-center">
                        <p className="text-muted-foreground">
                          {m.f1_home_season_concluded({
                            season: currentSeason,
                          })}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {nextRace.date}
                      </div>
                      <Link
                        to="/f1-fantasy/apex-team"
                        className="flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        {m.f1_home_prepare_team()}
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {m.f1_home_no_race_data()}
                    </p>
                  </div>
                )}
              </div>

              {/* Decorative glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-destructive/20 via-transparent to-primary/20 blur-3xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-8 border-y border-border/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickStats.map((stat) => (
              <div
                key={stat.label}
                className="flex items-center gap-4 p-4 rounded-lg bg-card/30 border border-border/50"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary">
                  <stat.icon className="h-5 w-5" />
                </div>
                <div>
                  {stat.loading ? (
                    <Skeleton className="h-6 w-12 mb-1" />
                  ) : (
                    <div className="font-display text-xl font-bold text-foreground">
                      {stat.value}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-10 md:py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 mb-6 md:mb-8">
            <Zap className="h-5 w-5 text-primary" />
            <h2 className="font-display text-xl md:text-2xl font-bold text-foreground">
              {m.f1_home_analytics_section()}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Apex Team */}
            <Link
              to="/f1-fantasy/apex-team"
              className="group relative p-4 md:p-6 rounded-xl border border-border bg-card/50 hover:border-primary/50 transition-all duration-300 card-hover"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity rounded-t-xl" />

              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4 group-hover:scale-110 transition-transform">
                <Calculator className="h-6 w-6" />
              </div>

              <h3 className="font-display text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                {m.f1_home_card_apex_title()}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {m.f1_home_card_apex_desc()}
              </p>

              <div className="flex items-center gap-1 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                {m.f1_home_launch_tool()}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Budget Boost */}
            <Link
              to="/f1-fantasy/budget-boost"
              className="group relative p-4 md:p-6 rounded-xl border border-border bg-card/50 hover:border-primary/50 transition-all duration-300 card-hover"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-success to-primary opacity-0 group-hover:opacity-100 transition-opacity rounded-t-xl" />

              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-success/10 text-success mb-4 group-hover:scale-110 transition-transform">
                <TrendingUp className="h-6 w-6" />
              </div>

              <h3 className="font-display text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                {m.f1_home_card_budget_title()}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {m.f1_home_card_budget_desc()}
              </p>

              <div className="flex items-center gap-1 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                {m.f1_home_launch_tool()}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Statistics */}
            <Link
              to="/f1-fantasy/statistics"
              className="group relative p-4 md:p-6 rounded-xl border border-border bg-card/50 hover:border-primary/50 transition-all duration-300 card-hover"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-secondary to-primary opacity-0 group-hover:opacity-100 transition-opacity rounded-t-xl" />

              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-secondary/10 text-secondary mb-4 group-hover:scale-110 transition-transform">
                <BarChart3 className="h-6 w-6" />
              </div>

              <h3 className="font-display text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                {m.f1_nav_statistics()}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {m.f1_home_card_stats_desc()}
              </p>

              <div className="flex items-center gap-1 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                {m.f1_home_launch_tool()}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Standings */}
            <Link
              to="/f1-fantasy/standings"
              search={{ tab: "drivers" }}
              className="group relative p-4 md:p-6 rounded-xl border border-border bg-card/50 hover:border-primary/50 transition-all duration-300 card-hover"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent to-destructive opacity-0 group-hover:opacity-100 transition-opacity rounded-t-xl" />

              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-accent/10 text-accent mb-4 group-hover:scale-110 transition-transform">
                <Trophy className="h-6 w-6" />
              </div>

              <h3 className="font-display text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                {m.f1_nav_standings()}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {m.f1_home_card_standings_desc()}
              </p>

              <div className="flex items-center gap-1 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                {m.f1_home_launch_tool()}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Fantasy Points */}
            <Link
              to="/f1-fantasy/fantasy-points"
              className="group relative p-4 md:p-6 rounded-xl border border-border bg-card/50 hover:border-primary/50 transition-all duration-300 card-hover"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-destructive to-primary opacity-0 group-hover:opacity-100 transition-opacity rounded-t-xl" />

              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-destructive/10 text-destructive mb-4 group-hover:scale-110 transition-transform">
                <Trophy className="h-6 w-6" />
              </div>

              <h3 className="font-display text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                {m.f1_nav_fantasy_points()}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {m.f1_home_card_points_desc()}
              </p>

              <div className="flex items-center gap-1 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                {m.f1_home_launch_tool()}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Drivers */}
      <section className="py-10 md:py-16 border-t border-border/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-secondary" />
              <h2 className="font-display text-xl md:text-2xl font-bold text-foreground">
                {m.f1_home_top_performers()}
              </h2>
            </div>
            <Link
              to="/f1-fantasy/standings"
              search={{ tab: "drivers" }}
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              {m.f1_home_view_standings()}
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {standingsLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="relative p-5 rounded-xl border border-border bg-card/50"
                >
                  <Skeleton className="absolute top-4 right-4 w-8 h-8 rounded-full" />
                  <Skeleton className="w-10 h-1 rounded-full mb-4" />
                  <Skeleton className="h-6 w-32 mb-2" />
                  <Skeleton className="h-4 w-24 mb-4" />
                  <div className="flex items-center justify-between">
                    <div>
                      <Skeleton className="h-3 w-12 mb-1" />
                      <Skeleton className="h-5 w-8" />
                    </div>
                    <div>
                      <Skeleton className="h-3 w-8 mb-1" />
                      <Skeleton className="h-5 w-6" />
                    </div>
                    <div>
                      <Skeleton className="h-3 w-12 mb-1" />
                      <Skeleton className="h-5 w-10" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : topDrivers.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {topDrivers.map((driver) => (
                <div
                  key={driver.name}
                  className="relative p-5 rounded-xl border border-border bg-card/50 hover:border-primary/30 transition-all duration-300"
                >
                  {/* Position badge */}
                  <div className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground font-display font-bold text-sm">
                    {driver.position}
                  </div>

                  {/* Team color indicator */}
                  <div
                    className="w-10 h-1 rounded-full mb-4"
                    style={{ backgroundColor: driver.color }}
                  />

                  <h3 className="font-display text-lg font-semibold text-foreground mb-1">
                    {driver.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {driver.team}
                  </p>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">
                        {m.f1_standings_col_points()}
                      </div>
                      <div className="font-mono text-foreground font-medium">
                        {driver.points}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">
                        {m.f1_standings_col_wins()}
                      </div>
                      <div className="font-mono text-foreground">
                        {driver.wins}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">
                        {m.f1_standings_col_podiums()}
                      </div>
                      <div className="font-mono text-foreground">
                        {driver.podiums}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {m.f1_home_no_driver_data({ season: currentSeason })}
              </p>
            </div>
          )}
        </div>
      </section>
      <F1SeoContent />
      <AdPlacement className="mt-6" />
    </div>
  );
}
